import { registerBrick } from 'olympe';
import { ReactBrick } from '@olympeio/core';
import { combineLatest } from 'rxjs';
import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Divider,
    Tooltip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';


export default class ServiceAppMonitorView extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('Service App Tag', false),
            $.observe('Service App Name', false),
            $.observe('Latest Status', false),
            $.observe('Services', false)
        ]);
    }

    init($) {
        super.init($);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [appTag, appName, latestStatus, services] = props.values;
            const [openDialog, setOpenDialog] = useState(false);
            const [openRestartConfirm, setOpenRestartConfirm] = useState(false);

            const handleRefresh = useCallback(() => {
                $.trigger('Get Status');
                $.trigger('Get Services');
            }, []);

            // Refresh status every 5 seconds
            React.useEffect(() => {
                const interval = setInterval(() => {
                    $.trigger('Get Status');
                }, 15000);
                return () => clearInterval(interval);
            }, []);

            const replicas = latestStatus?.replicas || [];

            const ROW_H = 44;      // approx row height for size="small"
            const HEADER_H = 48;   // approx header height
            const MAX_TABLE_H = HEADER_H + ROW_H * 2; // header + 3 rows

            return (
                <Paper elevation={0} sx={{ p: 2, width: '100%', boxSizing: 'border-box' }}>
                    <Stack sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h6" fontWeight={600}>{appName}</Typography>
                        <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
                          <Button size="small" variant="outlined" startIcon={<InfoOutlinedIcon />} onClick={() => setOpenDialog(true)} disabled={!services || services.length === 0}>
                            WHAT'S RUNNING
                          </Button>
                          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                            REFRESH
                          </Button>
                          <Button size="small" variant="outlined" color="error" startIcon={<RestartAltIcon />} onClick={() => setOpenRestartConfirm(true)}>
                            RESTART
                          </Button>
                        </Stack>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{appTag || 'Unknown tag'}</Typography>
                    </Stack>

                    {/* Replicas Table (under Service App) */}
                    <Box>
                      <Divider sx={{ mb: 1 }} />
                      {replicas.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No replicas found</Typography>
                      ) : (
                        <TableContainer sx={{
                            maxHeight: MAX_TABLE_H,          // header + 3 rows
                            overflowY: 'auto',               // force scroll when overflow
                            overflowX: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            width: '100%',
                            boxSizing: 'border-box',
                        }}>
                          <Table
                            size="small"
                            stickyHeader
                            sx={{
                              tableLayout: 'fixed',
                              '& .MuiTableCell-root': { py: 0.5 }, // compact cells
                              // Enforce exact body row height
                              '& .MuiTableBody-root .MuiTableRow-root': { height: ROW_H, maxHeight: ROW_H },
                              // Enforce exact header height
                              '& .MuiTableHead-root .MuiTableRow-root': { height: HEADER_H, maxHeight: HEADER_H },
                              '& .MuiTableHead-root .MuiTableCell-root': { py: 0.5 },
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell>Replica ID</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Start Time</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {replicas.map((r) => (
                                <TableRow key={r.replicaId} hover>
                                  <TableCell>{r.replicaId}</TableCell>
                                  <TableCell>
                                    <Chip label={r.status} color={r.status === 'Up' ? 'success' : r.status === 'Down' ? 'error' : 'default'} size="small" />
                                  </TableCell>
                                  <TableCell>{r.startTime}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Restarts in past week: {latestStatus?.restarts?.length ?? 0}
                      </Typography>
                    </Box>

                    {/* Restart confirmation */}
                    <Dialog open={openRestartConfirm} onClose={() => setOpenRestartConfirm(false)} maxWidth="xs" fullWidth>
                      <DialogTitle>Restart Service App</DialogTitle>
                      <DialogContent dividers>
                        <Typography variant="body2">Are you sure you want to restart this service app?</Typography>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={() => setOpenRestartConfirm(false)}>Cancel</Button>
                        <Button color="error" variant="contained" onClick={() => {
                          $.trigger('Trigger Restart');
                          setOpenRestartConfirm(false);
                        }}>Restart</Button>
                      </DialogActions>
                    </Dialog>

                    {/* Services Dialog */}
                    <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>What's Running</DialogTitle>
                        <DialogContent dividers sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {Array.isArray(services) && services.length > 0 ? (
                                services
                                    .slice()
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((s, index) => (
                                        <Box key={index} sx={{ mb: 2 }}>
                                            <Typography variant="subtitle1" fontWeight={600}>{s.name}</Typography>
                                            <Typography variant="body2" color="text.secondary">Type: {s.type}</Typography>
                                            {s.summary && (
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>{s.summary}</Typography>
                                            )}
                                            <Divider sx={{ mt: 1 }} />
                                        </Box>
                                    ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">No services found</Typography>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenDialog(false)}>Close</Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            );
        };
    }
}


registerBrick('0199e3abe2a6442c6dbc', ServiceAppMonitorView);
